import { useEffect } from "react";

import { useParams } from "react-router-dom";

import ErrorPage from "../components/ErrorPage/ErrorPage";
import LoadingPage from "../components/LoadingPage/LoadingPage";
import TeamInvitationError from "../components/TeamInvitationError/TeamInvitationError";
import TeamInvitationSelfServeSignUp from "../components/TeamInvitationSelfServeSignUp/TeamInvitationSelfServeSignUp";
import { TeamInvitationSelfServeSSOSignup } from "../components/TeamInvitationSelfServeSSOSignup/TeamInvitationSelfServeSSOSignup";
import TeamInvitationSuccess from "../components/TeamInvitationSuccess/TeamInvitationSuccess";
import useAccount from "../hooks/useAccount";
import { useAcceptInvitation, useInvitation, useInvitationSignUp } from "../hooks/useInvitation";
import { useWindowSize, ViewportType } from "../hooks/useWindowSize";
import { ServiceErrorCode, SignupSource } from "../types";

const TeamInvitationSelfServeSignUpContainer = () => {
  const { invitationHash } = useParams();
  const { isLoading: isAccountLoading } = useAccount();
  const { viewportType } = useWindowSize();
  const isMobile = viewportType === ViewportType.Mobile;

  const {
    data: invitationData,
    isLoading: isInvitationLoading,
    isError: isInvitationError,
    error: invitationError,
  } = useInvitation(invitationHash);

  const {
    isSuccess: isAcceptInvitationSuccess,
    mutate: mutateAcceptInvitation,
    isPending: isAcceptInvitationLoading,
  } = useAcceptInvitation(invitationHash);

  const {
    isError: isSignUpError,
    mutate: mutateSignUp,
    isPending: isSignUpLoading,
    isSuccess: isSignUpSuccess,
  } = useInvitationSignUp(invitationHash, {
    signupSource: SignupSource.TeamInvite,
    isMobile,
  });

  useEffect(() => {
    if (invitationData?.userExists) {
      mutateAcceptInvitation();
    }
  }, [invitationData, mutateAcceptInvitation]);

  if (isAccountLoading || isInvitationLoading || isAcceptInvitationLoading) {
    return <LoadingPage />;
  }

  if (isInvitationError || invitationData === undefined) {
    if (invitationError?.hasCode(ServiceErrorCode.InvalidUuid)) {
      return <TeamInvitationError errorType="Invalid" />;
    }
    if (invitationError?.hasCode(ServiceErrorCode.TeamInvitationAlreadyAccepted)) {
      return <TeamInvitationError errorType="AlreadyAccepted" />;
    }
    if (invitationError?.hasCode(ServiceErrorCode.TeamInvitationNotFound)) {
      return <TeamInvitationError errorType="NotAvailable" />;
    }
    return <ErrorPage />;
  }

  if (isAcceptInvitationSuccess || isSignUpSuccess) {
    return <TeamInvitationSuccess teamName={invitationData.teamName} />;
  }

  if (!invitationData.userExists && invitationData.redirectURL) {
    return <TeamInvitationSelfServeSSOSignup redirectURL={invitationData.redirectURL} />;
  }

  return (
    <TeamInvitationSelfServeSignUp
      invitation={invitationData}
      submit={mutateSignUp}
      isSubmitting={isSignUpLoading}
      errorMessage={isSignUpError ? "Something went wrong" : ""}
    />
  );
};

export default TeamInvitationSelfServeSignUpContainer;
